import { createRequire } from 'node:module';
import { dirname } from 'node:path';
import { createCommand } from '@commander-js/extra-typings';
import { execa } from 'execa';
import type { AstroCommandServices } from '@wix/cli-command-contract';
import { CliError, CliErrorCode } from '@wix/cli-error';

const require = createRequire(import.meta.url);

/**
 * Resolves the on-disk directory of the pinned `@wix/agent-skills` package
 * (declared as a dependency of `@wix/cli`) so the `skills` CLI can read skill
 * content from it.
 */
function resolveSkillsSource(): string {
  return dirname(require.resolve('@wix/agent-skills/package.json'));
}

export const skillsAddCommand = (
  services: AstroCommandServices,
  cliVersion: string
) => {
  /**
   * Test-only escape hatch:
   * The CLI driver runs the CLI through `fnm exec`, which can rewrite PATH.
   * In tests we want deterministic interception of `npx` without relying on PATH
   * ordering, so we allow overriding the executable via env var.
   */
  const npxExecutable = process.env.WIX_CLI_NPX_PATH ?? 'npx';

  return createCommand('add')
    .description('Add Wix skills to your project')
    .action(async (_, command) => {
      const { runCommand } = await import('./render-command.js');

      // The `skills` CLI uses -y/--yes to skip prompts.
      const args = [
        '--yes',
        'skills',
        'add',
        resolveSkillsSource(),
        '-y',
      ] as const;

      await runCommand(command, services, cliVersion, async (model) => {
        try {
          await execa(npxExecutable, [...args], {
            cwd: model.projectFolder,
            stdio: 'inherit',
          });
        } catch (e) {
          throw new CliError({
            code: CliErrorCode.FailedToRunExternalCommand({
              file: npxExecutable,
              args: [...args],
            }),
            cause: e,
          });
        }
      });
    });
};
